/**
 * Shared utilities for drilldown modals.
 *
 * Consolidates common functions used across DrilldownModal,
 * OTDDrilldownModal, and OTIFDrilldownModal.
 */

import { ExternalLink } from 'lucide-react';
import React from 'react';

// =============================================================================
// TYPES
// =============================================================================

export interface LinkableFieldConfig {
  urlPattern: (value: string, record: Record<string, unknown>) => string;
  tab?: string;
}

export interface TabConfig {
  key: string;
  label: string;
  filter: Record<string, string>;
}

export interface DrilldownField {
  field: string;
  label: string;
}

// =============================================================================
// LINKABLE FIELDS CONFIGURATION
// =============================================================================

/**
 * Common linkable fields shared across all drilldown modals
 */
export const COMMON_LINKABLE_FIELDS: Record<string, LinkableFieldConfig> = {
  grn_number: {
    urlPattern: (value) => `/purchasing?tab=delivery&search=${encodeURIComponent(value)}`,
    tab: 'delivery',
  },
  po_line__po__po_number: {
    urlPattern: (value) => `/purchasing?tab=po_lines&search=${encodeURIComponent(value)}`,
    tab: 'po_lines',
  },
  po_line__line_number: {
    urlPattern: (_value, record) => {
      const poNumber = String(record['po_line__po__po_number'] ?? '');
      return `/purchasing?tab=po_lines&search=${encodeURIComponent(poNumber)}`;
    },
    tab: 'po_lines',
  },
  po__po_number: {
    urlPattern: (value) => `/purchasing?tab=po_lines&search=${encodeURIComponent(value)}`,
    tab: 'po_lines',
  },
  po_number: {
    urlPattern: (value) => `/purchasing?tab=po_lines&search=${encodeURIComponent(value)}`,
    tab: 'po_lines',
  },
  grn__po_line__po__po_number: {
    urlPattern: (value) => `/purchasing?tab=po_lines&search=${encodeURIComponent(value)}`,
    tab: 'po_lines',
  },
  ncr_number: {
    urlPattern: (value) => `/quality?tab=ncrs&search=${encodeURIComponent(value)}`,
    tab: 'ncrs',
  },
};

/**
 * Build a deep-link URL to the PO Lines tab for an OTIF drill-down row.
 *
 * CR-4666: Pass `line=<id>` so POLinesTab opens the exact line (was falling
 * back to the first line of the PO).
 * CR-4667: Also pass `supplier=<id>` so the list filter can disambiguate
 * POs whose number collides across different suppliers. Without the
 * supplier id, `po__po_number__icontains=<number>` can resolve to a
 * different PO than the one the user clicked in the drill-down.
 */
function buildOtifPoLinesUrl(record: Record<string, unknown>, poNumber?: string): string {
  const params = new URLSearchParams({ tab: 'po_lines' });
  if (poNumber) params.set('search', poNumber);
  const lineId = String(record['id'] ?? '');
  if (lineId) params.set('line', lineId);
  const supplierId = String(record['po_supplier_id'] ?? '');
  if (supplierId) params.set('supplier', supplierId);
  return `/purchasing?${params.toString()}`;
}

/**
 * OTIF-specific linkable fields
 */
export const OTIF_LINKABLE_FIELDS: Record<string, LinkableFieldConfig> = {
  ...COMMON_LINKABLE_FIELDS,
  line_number: {
    urlPattern: (_value, record) => {
      const poNumber = String(record['po__po_number'] || record['po_line__po__po_number'] || '');
      return buildOtifPoLinesUrl(record, poNumber);
    },
    tab: 'po_lines',
  },
  po__po_number: {
    urlPattern: (value, record) => buildOtifPoLinesUrl(record, String(value || '')),
    tab: 'po_lines',
  },
};

// =============================================================================
// VALUE FORMATTING
// =============================================================================

/**
 * Format a value for display in drilldown tables
 */
export function formatDrilldownValue(value: unknown): string {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') {
    return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  // Check if it's a date string (ISO format)
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return new Date(value).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }
  return String(value);
}

/**
 * Get status badge styling for OTD status
 */
export function getOTDStatusStyle(status: string): string {
  const normalizedStatus = status.toLowerCase().replace(/\s+/g, '_');
  const styles: Record<string, string> = {
    on_time: 'bg-green-100 text-green-800',
    'on time': 'bg-green-100 text-green-800',
    early: 'bg-blue-100 text-blue-800',
    late: 'bg-red-100 text-red-800',
  };
  return styles[normalizedStatus] || 'bg-gray-100 text-gray-800';
}

/**
 * Get status badge styling for OTIF status
 */
export function getOTIFStatusStyle(value: unknown): { className: string; label: string } {
  // CR-4543: Match actual DB values — 'otif' and 'early' = pass, others = fail
  const isPass = value === true || value === 'True' || value === 'PASS' || value === 'otif' || value === 'early';
  return {
    className: isPass
      ? 'bg-green-100 text-green-800 hover:bg-green-200'
      : 'bg-red-100 text-red-800 hover:bg-red-200',
    label: isPass ? 'PASS' : 'FAILED',
  };
}

/**
 * Get styled label for GRN on_time_status field
 */
export function getOnTimeStatusStyle(value: unknown): { className: string; label: string } {
  const strValue = String(value || '').toLowerCase();
  switch (strValue) {
    case 'on_time':
      return { className: 'bg-green-100 text-green-800', label: 'On Time' };
    case 'early':
      return { className: 'bg-blue-100 text-blue-800', label: 'Early' };
    case 'late':
      return { className: 'bg-red-100 text-red-800', label: 'Late' };
    default:
      return { className: 'bg-gray-100 text-gray-800', label: value ? String(value) : '-' };
  }
}

// =============================================================================
// CELL RENDERING
// =============================================================================

export interface RenderCellOptions {
  fieldKey: string;
  value: unknown;
  record: Record<string, unknown>;
  linkableFields?: Record<string, LinkableFieldConfig>;
  onStatusClick?: (record: Record<string, unknown>) => void;
  statusField?: string;
}

/**
 * Create a link element for linkable fields.
 *
 * CR-5363 round 2: previously opened the deep-link in a new tab via
 * target="_blank". Customer testers reported the click "did nothing"
 * — the new tab was getting popup-blocked / opening behind the
 * drilldown / not noticed. Navigate in the same tab so the click is
 * unmistakably actioned. The drilldown modal is just a dashboard
 * surface; users can re-open it from the dashboard tile if needed.
 */
export function createFieldLink(
  url: string,
  displayValue: string
): React.ReactElement {
  return React.createElement(
    'a',
    {
      href: url,
      className: 'text-primary hover:underline inline-flex items-center gap-1 font-medium',
      onClick: (e: React.MouseEvent) => e.stopPropagation(),
    },
    displayValue,
    React.createElement(ExternalLink, { className: 'h-3 w-3' })
  );
}

/**
 * Create an OTD status badge
 */
export function createOTDStatusBadge(value: string): React.ReactElement {
  const { className, label } = getOnTimeStatusStyle(value);
  return React.createElement(
    'span',
    { className: `px-2 py-1 rounded text-xs font-medium ${className}` },
    label
  );
}

/**
 * Create an OTIF status button
 */
export function createOTIFStatusButton(
  value: unknown,
  onClick: () => void
): React.ReactElement {
  const { className, label } = getOTIFStatusStyle(value);
  return React.createElement(
    'button',
    {
      onClick,
      className: `px-2 py-1 rounded text-xs font-medium ${className}`,
    },
    label
  );
}

// =============================================================================
// EXPORT HANDLING
// =============================================================================

/**
 * Download exported data as a file
 */
export function downloadExportedData(
  data: BlobPart,
  filename: string,
  format: 'csv' | 'xlsx'
): void {
  const url = window.URL.createObjectURL(new Blob([data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `${filename}.${format}`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

// =============================================================================
// TAB CONFIGURATIONS
// =============================================================================

export const OTD_TABS: TabConfig[] = [
  { key: 'on_time', label: 'ON TIME', filter: { otd_status: 'on_time' } },
  { key: 'early', label: 'EARLY', filter: { otd_status: 'early' } },
  { key: 'late', label: 'LATE', filter: { otd_status: 'late' } },
  { key: 'total', label: 'TOTAL', filter: {} },
];

export const OTIF_TABS: TabConfig[] = [
  { key: 'failed', label: 'FAILED OTIF LINES', filter: { otif_status: 'failed' } },
  { key: 'arrears', label: 'ARREAR LINES', filter: { otif_status: 'arrears' } },
  { key: 'positive', label: 'PASSED OTIF LINES', filter: { otif_status: 'positive' } },
  { key: 'total', label: 'TOTAL LINES', filter: {} },
];
